// PendingRotationBanner — top-of-shell amber strip surfacing vaults
// whose master rotation failed mid-flight. Reads from
// `useMasterSession.appConfig.vaults` and renders only when at least
// one vault has a non-null `pending_rotation`.
//
// Two actions: "Retry now" opens a small dialog asking the user to
// re-type both the OLD master (vaults' current password — the one
// before the rotation) and the CURRENT master (the post-rotation
// master, which the user types to authorize the re-wrap and to feed
// back into `retryPendingRotations`). The "Dismiss" button hides the
// banner for the current session via `useUi.pendingRotationBannerDismissed`
// — no persistence per the plaintext-at-rest rule, so it re-appears
// on every full reload as long as any pending_rotation remains.

import { useState } from "react";
import { useMasterSession } from "../../stores/masterSession";
import { useUi } from "../../stores/ui";
import { useVault } from "../../hooks/useVault";
import {
  retryPendingRotations,
  type RotationProgress,
} from "../../lib/masterRotation";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { MasterRotationModal } from "../lifecycle/MasterRotationModal";

const NAMES_IN_BANNER = 2;

export function PendingRotationBanner() {
  const appConfig = useMasterSession((s) => s.master?.appConfig);
  const dismissed = useUi((s) => s.pendingRotationBannerDismissed);
  const dismissBanner = useUi((s) => s.dismissPendingRotationBanner);
  const worker = useVault();
  const [retryOpen, setRetryOpen] = useState(false);
  const [oldPass, setOldPass] = useState("");
  const [currentPass, setCurrentPass] = useState("");
  const [progress, setProgress] = useState<RotationProgress | null>(null);

  if (!appConfig) return null;
  const pending = appConfig.vaults.filter(
    (v) => v.pending_rotation !== null,
  );
  if (pending.length === 0) return null;
  if (dismissed) return null;

  const previewNames = pending
    .slice(0, NAMES_IN_BANNER)
    .map((v) => v.display_name)
    .join(", ");
  const remainder = pending.length - NAMES_IN_BANNER;
  const summary =
    remainder > 0
      ? `${pending.length} vaults pending: ${previewNames} +${remainder} more`
      : `${pending.length} ${pending.length === 1 ? "vault" : "vaults"} pending: ${previewNames}`;

  function onStart(): void {
    if (oldPass.length === 0 || currentPass.length === 0) return;
    setRetryOpen(false);
    void (async () => {
      try {
        await retryPendingRotations(worker, oldPass, currentPass, {
          onProgress: setProgress,
        });
      } catch (err) {
        // `retryPendingRotations` already emits an `aborted` phase on
        // its known failure paths (saveAppConfig outage, etc.) before
        // rethrowing — see `masterRotation.ts`. As a safety net for
        // unexpected throws that bypass that contract, we transition
        // the modal to an `aborted` phase here too so the user is
        // never left staring at an in-flight spinner with no Close
        // button. If `progress` was never set (modal not yet shown),
        // we leave it null so the banner just doesn't render the
        // modal at all.
        const message = err instanceof Error ? err.message : String(err);
        setProgress((prev) => {
          if (prev === null) return null;
          if (prev.phase.kind === "aborted" || prev.phase.kind === "done") {
            return prev;
          }
          return {
            phase: {
              kind: "aborted",
              reason: "save-config-failed",
              error: message,
            },
            results: prev.results,
          };
        });
      } finally {
        setOldPass("");
        setCurrentPass("");
      }
    })();
  }

  return (
    <>
      <div className="border-b border-warn/40 bg-warn/10 px-4 py-2 text-[13px] text-warn">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
          <span>{summary} — pending master-sync retry</span>
          <button
            type="button"
            onClick={() => setRetryOpen(true)}
            className="ml-auto rounded-md bg-accent px-3 py-1 text-[12px] font-medium text-white"
          >
            Retry now
          </button>
          <button
            type="button"
            onClick={dismissBanner}
            className="text-[12px] text-text-soft underline hover:text-text"
          >
            Dismiss
          </button>
        </div>
      </div>

      {retryOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Retry pending rotations"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-bg-raised p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-medium tracking-tight">
              Retry master sync
            </h2>
            <p className="mb-4 text-[13px] text-text-soft">
              Type the OLD master (the one used to create the pending vaults)
              and the CURRENT master (the new one set during rotation).
            </p>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="retry-old-master"
                  className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
                >
                  Old master
                </label>
                <PassphraseInput
                  id="retry-old-master"
                  autoCompleteMode="current-password"
                  aria-label="Old master"
                  value={oldPass}
                  onChange={(e) => setOldPass(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="retry-current-master"
                  className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
                >
                  Current master
                </label>
                <PassphraseInput
                  id="retry-current-master"
                  autoCompleteMode="current-password"
                  aria-label="Current master"
                  value={currentPass}
                  onChange={(e) => setCurrentPass(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRetryOpen(false);
                  setOldPass("");
                  setCurrentPass("");
                }}
                className="rounded-md border border-border px-4 py-2 text-sm text-text-soft"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onStart}
                disabled={oldPass.length === 0 || currentPass.length === 0}
                className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                Start
              </button>
            </div>
          </div>
        </div>
      )}

      {progress && (
        <MasterRotationModal
          progress={progress}
          onClose={() => setProgress(null)}
        />
      )}
    </>
  );
}
